// contexts/GameContext.js - Updated with useCallback for stable functions
import React, { createContext, useContext, useState, useReducer, useEffect, useCallback } from 'react';

// Create context
const GameContext = createContext();

// Initial state
const initialGameState = {
  currentTopic: null,
  score: 0,
  timeLeft: 300,
  currentGameIndex: 0,
  gameSequence: [],
  progress: 0,
  soundEnabled: true,
  incorrectQueue: [],
  correctCount: {}, 
  incorrectCount: {},
  metrics: {
    startTime: null,
    totalQuestionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    timePerQuestion: [],
    wordsLearned: new Set(),
    wordStats: {}
  }
};

// Reducer function
function gameReducer(state, action) {
  switch (action.type) {
    case 'INIT_GAME':
      return {
        ...state,
        currentTopic: action.payload.topic,
        score: 0,
        timeLeft: action.payload.timeInSeconds,
        currentGameIndex: 0,
        gameSequence: action.payload.gameSequence,
        progress: 0,
        metrics: {
          startTime: new Date(),
          totalQuestionsAnswered: 0,
          correctAnswers: 0,
          incorrectAnswers: 0,
          timePerQuestion: [],
          wordsLearned: new Set(),
          wordStats: {}
        }
      };
    case 'UPDATE_SCORE':
      return { ...state, score: state.score + action.payload };
    case 'UPDATE_TIME':
      return { ...state, timeLeft: state.timeLeft - 1 };
    case 'SET_TIME':
      return { ...state, timeLeft: action.payload };
    case 'NEXT_GAME':
      return { 
        ...state, 
        currentGameIndex: state.currentGameIndex + 1,
        progress: ((state.currentGameIndex + 1) / state.gameSequence.length) * 100
      };
    case 'RESET_GAME_INDEX':
      return { ...state, currentGameIndex: 0 };
    case 'UPDATE_GAME_SEQUENCE':
      return { ...state, gameSequence: action.payload };
    case 'TOGGLE_SOUND':
      return { ...state, soundEnabled: !state.soundEnabled };
    case 'ADD_TO_INCORRECT_QUEUE':
      if (state.incorrectQueue.some(word => word.id === action.payload.id)) {
        return state;
      }
      return { 
        ...state, 
        incorrectQueue: [...state.incorrectQueue, action.payload],
        incorrectCount: { 
          ...state.incorrectCount, 
          [action.payload.id]: (state.incorrectCount[action.payload.id] || 0) + 1 
        },
        correctCount: { 
          ...state.correctCount, 
          [action.payload.id]: 0 
        } 
      };
    case 'REMOVE_FROM_INCORRECT_QUEUE':
      return { 
        ...state, 
        incorrectQueue: state.incorrectQueue.filter(word => word.id !== action.payload) 
      };
    case 'INCREMENT_CORRECT_COUNT':
      return { 
        ...state, 
        correctCount: { 
          ...state.correctCount, 
          [action.payload]: (state.correctCount[action.payload] || 0) + 1 
        } 
      };
    case 'UPDATE_METRICS':
      return { 
        ...state, 
        metrics: {
          ...state.metrics,
          ...action.payload
        } 
      };
    case 'ADD_WORD_LEARNED':
      const newWordsLearned = new Set(state.metrics.wordsLearned);
      newWordsLearned.add(action.payload);
      return {
        ...state,
        metrics: {
          ...state.metrics,
          wordsLearned: newWordsLearned
        }
      };
    default:
      return state;
  }
}

// Provider component
export function GameProvider({ children, initialData }) {
  const [gameData] = useState(initialData || {
    config: {
      pointsPerCorrectAnswer: 10,
      questionsPerGame: 5,
      wordRepetitionCount: 2
    },
    topics: []
  });
  
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState);

  // Use useCallback for stable function references
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Memoize getRandomOptions to avoid recreating this function on each render
  const getRandomOptions = useCallback((correctWord, numWrong) => {
    if (!gameState.currentTopic) return [correctWord];
    
    const options = [correctWord];
    const availableWords = gameState.currentTopic.words.filter(
      word => word.id !== correctWord.id
    );
    
    // Shuffle available words
    const shuffledWords = shuffleArray([...availableWords]);
    
    // Add wrong options
    for (let i = 0; i < Math.min(numWrong, shuffledWords.length); i++) {
      options.push(shuffledWords[i]);
    }
    
    // Shuffle options
    return shuffleArray(options);
  }, [gameState.currentTopic, shuffleArray]);

  // Mock play sound function
  const playCorrectSound = useCallback(() => {
    if (gameState.soundEnabled) {
      console.log("Played correct sound");
    }
  }, [gameState.soundEnabled]);

  // Initialize game function
  const initGame = useCallback((topicId) => {
    console.log("Initializing game with topic:", topicId);
    
    // Find topic in gameData
    const topic = gameData.topics.find(t => t.id === topicId);
    if (!topic) return;
    
    const timeInSeconds = gameData.config.sessionDurationMinutes
      ? gameData.config.sessionDurationMinutes * 60
      : 300; // Default to 5 minutes
    
    dispatch({
      type: 'INIT_GAME',
      payload: {
        topic,
        timeInSeconds,
        gameSequence: [] // You'd compute this based on your game logic
      }
    });
  }, [gameData]);

  return (
    <GameContext.Provider
      value={{
        gameData,
        gameState,
        dispatch,
        getRandomOptions,
        shuffleArray,
        playCorrectSound,
        initGame
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

// Custom hook to use the context
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}